import { Card, Descriptions, Tag, Timeline, Table, Space, Typography } from 'antd';
import { EnvironmentOutlined, GiftOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface PostageDestination {
  id: number;
  order: number;
  address: string;
  recipientName: string;
  recipientPhone: string;
  deliveryNote?: string;
  status: 'PENDING' | 'IN_TRANSIT' | 'DELIVERED' | 'FAILED';
  deliveredAt?: string;
  failureReason?: string;
}

interface PostageTrip {
  id: number;
  packageDescription: string;
  packageWeight?: number;
  packageSize?: 'SMALL' | 'MEDIUM' | 'LARGE' | 'EXTRA_LARGE';
  isFragile: boolean;
  requiresSignature: boolean;
  insuranceValue?: number;
  destinations: PostageDestination[];
}

interface PostageTripDetailsProps {
  trip: PostageTrip;
}

const packageSizeLabels: Record<string, string> = {
  SMALL: "Kichik (30x20x15 cm gacha)",
  MEDIUM: "O'rta (50x40x30 cm gacha)",
  LARGE: "Katta (80x60x50 cm gacha)",
  EXTRA_LARGE: "Juda katta",
};

const destinationStatusColors: Record<string, string> = {
  PENDING: 'default',
  IN_TRANSIT: 'processing',
  DELIVERED: 'success',
  FAILED: 'error',
};

const destinationStatusLabels: Record<string, string> = {
  PENDING: 'Kutilmoqda',
  IN_TRANSIT: 'Yo\'lda',
  DELIVERED: 'Yetkazildi',
  FAILED: 'Muvaffaqiyatsiz',
};

export const PostageTripDetails = ({ trip }: PostageTripDetailsProps) => {
  const destinationColumns = [
    {
      title: '#',
      dataIndex: 'order',
      key: 'order',
      width: 50,
    },
    {
      title: 'Manzil',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
    },
    {
      title: 'Qabul qiluvchi',
      key: 'recipient',
      render: (_: unknown, record: PostageDestination) => (
        <div>
          <div>{record.recipientName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.recipientPhone}
          </Text>
        </div>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: string) => (
        <Tag color={destinationStatusColors[status]}>
          {destinationStatusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: 'Yetkazilgan vaqt',
      dataIndex: 'deliveredAt',
      key: 'deliveredAt',
      width: 150,
      render: (date: string | undefined) => {
        if (!date) return '-';
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, '0');
        const mins = String(d.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${mins}`;
      },
    },
  ];

  const getDeliveryProgress = () => {
    const total = trip.destinations?.length || 0;
    const delivered = trip.destinations?.filter(d => d.status === 'DELIVERED').length || 0;
    return { total, delivered };
  };

  const progress = getDeliveryProgress();

  return (
    <>
      {/* Package Information */}
      <Card
        title={
          <Space>
            <GiftOutlined />
            <span>Jo'natma ma'lumotlari</span>
          </Space>
        }
        style={{ marginTop: 24 }}
      >
        <Descriptions column={2}>
          <Descriptions.Item label="Tavsif" span={2}>
            {trip.packageDescription || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Vazn">
            {trip.packageWeight ? `${trip.packageWeight} kg` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="O'lcham">
            {trip.packageSize ? packageSizeLabels[trip.packageSize] : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Mo'rt">
            {trip.isFragile ? (
              <Tag color="orange">Ha</Tag>
            ) : (
              <Tag>Yo'q</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Imzo talab qilinadi">
            {trip.requiresSignature ? (
              <Tag color="blue">Ha</Tag>
            ) : (
              <Tag>Yo'q</Tag>
            )}
          </Descriptions.Item>
          {trip.insuranceValue && (
            <Descriptions.Item label="Sug'urta qiymati">
              {new Intl.NumberFormat('uz-UZ').format(trip.insuranceValue)} so'm
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Delivery Progress */}
      <Card
        title={
          <Space>
            <EnvironmentOutlined />
            <span>Yetkazish manzillari</span>
            <Tag color="blue">
              {progress.delivered}/{progress.total} yetkazildi
            </Tag>
          </Space>
        }
        style={{ marginTop: 24 }}
      >
        <Table
          dataSource={trip.destinations || []}
          columns={destinationColumns}
          rowKey="id"
          pagination={false}
          size="small"
          expandable={{
            expandedRowRender: (record: PostageDestination) => (
              <div style={{ padding: '8px 0' }}>
                {record.deliveryNote && (
                  <p>
                    <Text strong>Eslatma: </Text>
                    {record.deliveryNote}
                  </p>
                )}
                {record.failureReason && (
                  <p style={{ color: '#E74C3C' }}>
                    <Text strong>Muvaffaqiyatsizlik sababi: </Text>
                    {record.failureReason}
                  </p>
                )}
              </div>
            ),
            rowExpandable: (record: PostageDestination) =>
              !!(record.deliveryNote || record.failureReason),
          }}
        />
      </Card>

      {/* Delivery Timeline */}
      {trip.destinations && trip.destinations.length > 0 && (
        <Card title="Yetkazish vaqt chizig'i" style={{ marginTop: 24 }}>
          <Timeline
            items={trip.destinations.map((dest) => ({
              color:
                dest.status === 'DELIVERED'
                  ? 'green'
                  : dest.status === 'FAILED'
                    ? 'red'
                    : dest.status === 'IN_TRANSIT'
                      ? 'blue'
                      : 'gray',
              dot:
                dest.status === 'DELIVERED' ? (
                  <CheckCircleOutlined />
                ) : dest.status === 'IN_TRANSIT' ? (
                  <ClockCircleOutlined />
                ) : undefined,
              children: (
                <div>
                  <div>
                    <Text strong>#{dest.order}</Text> - {dest.address}
                  </div>
                  <div>
                    <Text type="secondary">
                      {dest.recipientName} ({dest.recipientPhone})
                    </Text>
                  </div>
                  <Tag
                    color={destinationStatusColors[dest.status]}
                    style={{ marginTop: 4 }}
                  >
                    {destinationStatusLabels[dest.status]}
                  </Tag>
                </div>
              ),
            }))}
          />
        </Card>
      )}
    </>
  );
};
